import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Modal, Button } from '../../components';
import { createGiftCheckoutSessionApi } from '../../util/api';
import css from './GiftSubscriptionModal.module.css';

const MONTH_PRESETS = [1, 3, 6, 12];
const MESSAGE_MAX_LENGTH = 300;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// tier: { id, name, price (dollars), billingType: 'one_time'|'recurring', billingInterval }
export default function GiftSubscriptionModal({ isOpen, onClose, tier }) {
  const isRecurring = tier?.billingType !== 'one_time';
  const [months, setMonths] = useState(1);
  const [customMonths, setCustomMonths] = useState('');
  const [useCustomMonths, setUseCustomMonths] = useState(false);
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');
  const [noRefundAcknowledged, setNoRefundAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const effectiveMonths = useCustomMonths ? Math.max(1, Number(customMonths) || 0) : months;
  const totalPrice = isRecurring ? (tier?.price || 0) * effectiveMonths : tier?.price || 0;

  const canSubmit =
    tier?.id &&
    recipientName.trim() &&
    EMAIL_PATTERN.test(recipientEmail.trim()) &&
    noRefundAcknowledged &&
    (!isRecurring || effectiveMonths >= 1) &&
    !submitting;

  const resetAndClose = () => {
    setMonths(1);
    setCustomMonths('');
    setUseCustomMonths(false);
    setRecipientName('');
    setRecipientEmail('');
    setMessage('');
    setNoRefundAcknowledged(false);
    setError('');
    onClose();
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await createGiftCheckoutSessionApi({
        tierId: tier.id,
        giftType: isRecurring ? 'months' : 'one_time',
        durationInMonths: isRecurring ? effectiveMonths : undefined,
        recipientName: recipientName.trim(),
        recipientEmail: recipientEmail.trim(),
        message: message.trim() || undefined,
        noRefundAcknowledged: true,
      });
      const checkoutUrl = response?.checkoutUrl;
      if (!checkoutUrl) {
        throw new Error('Checkout URL was not returned.');
      }
      window.location.href = checkoutUrl;
    } catch (err) {
      setError(err?.error || err?.message || 'Unable to start gift checkout right now.');
      setSubmitting(false);
    }
  };

  const messageRemaining = MESSAGE_MAX_LENGTH - message.length;

  return (
    <Modal
      id="giftSubscriptionModal"
      className={css.modalRoot}
      isOpen={isOpen}
      onClose={resetAndClose}
      usePortal
      hideDefaultClose
      contentClassName={css.modalContent}
      onManageDisableScrolling={() => {}}
    >
      <div className={css.header}>
        <h2 className={css.title}>Gift {tier?.name || 'this subscription'}</h2>
        <button type="button" className={css.closeButton} onClick={resetAndClose} aria-label="Close">
          <X size={20} strokeWidth={2} aria-hidden />
        </button>
      </div>

      <form onSubmit={handleSubmit} className={css.form}>
        {isRecurring && (
          <div className={css.field}>
            <label className={css.label}>How long should the gift last?</label>
            <div className={css.presetRow}>
              {MONTH_PRESETS.map(m => (
                <button
                  key={m}
                  type="button"
                  className={`${css.presetButton} ${!useCustomMonths && months === m ? css.presetButtonActive : ''}`}
                  onClick={() => {
                    setUseCustomMonths(false);
                    setMonths(m);
                  }}
                >
                  {m} {m === 1 ? 'month' : 'months'}
                </button>
              ))}
              <button
                type="button"
                className={`${css.presetButton} ${useCustomMonths ? css.presetButtonActive : ''}`}
                onClick={() => setUseCustomMonths(true)}
              >
                Custom
              </button>
            </div>
            {useCustomMonths && (
              <input
                type="number"
                min="1"
                className={css.input}
                placeholder="Number of months"
                value={customMonths}
                onChange={e => setCustomMonths(e.target.value)}
                autoFocus
              />
            )}
            <p className={css.helperText}>
              The recipient keeps this tier at no charge for {effectiveMonths} month
              {effectiveMonths === 1 ? '' : 's'}, then it simply ends — no auto-renewal.
            </p>
          </div>
        )}

        <div className={css.field}>
          <label className={css.label} htmlFor="giftRecipientName">
            Recipient's name
          </label>
          <input
            id="giftRecipientName"
            type="text"
            className={css.input}
            value={recipientName}
            onChange={e => setRecipientName(e.target.value)}
            placeholder="Jane Doe"
            required
          />
        </div>

        <div className={css.field}>
          <label className={css.label} htmlFor="giftRecipientEmail">
            Recipient's email
          </label>
          <input
            id="giftRecipientEmail"
            type="email"
            className={css.input}
            value={recipientEmail}
            onChange={e => setRecipientEmail(e.target.value)}
            placeholder="jane@example.com"
            required
          />
          <p className={css.helperText}>
            The gift activates automatically as soon as they sign up (or log in) on Coachify with
            this exact email address.
          </p>
        </div>

        <div className={css.field}>
          <label className={css.label} htmlFor="giftMessage">
            Message (optional)
          </label>
          <textarea
            id="giftMessage"
            className={css.textarea}
            value={message}
            onChange={e => setMessage(e.target.value.slice(0, MESSAGE_MAX_LENGTH))}
            placeholder="Say something nice..."
            rows={3}
            maxLength={MESSAGE_MAX_LENGTH}
          />
          <p className={css.charCount}>{messageRemaining} characters left</p>
        </div>

        <div className={css.summaryRow}>
          <span>Gift value</span>
          <span className={css.summaryPrice}>
            ${Number.isInteger(totalPrice) ? totalPrice : totalPrice.toFixed(2)}
          </span>
        </div>
        <p className={css.helperText}>
          Taxes &amp; platform fee applies. On gifts under $30, Stripe’s processing fee (2.9% + 30¢, a third party) is added too. Each one is shown before you pay on the next screen.
        </p>

        <label className={css.checkboxRow}>
          <input
            type="checkbox"
            checked={noRefundAcknowledged}
            onChange={e => setNoRefundAcknowledged(e.target.checked)}
          />
          <span>
            I understand this gift is a <strong>final sale</strong> — Coachify gift purchases are
            not eligible for refunds or cancellations.
          </span>
        </label>

        {error ? <p className={css.errorText}>{error}</p> : null}

        <Button type="submit" disabled={!canSubmit} inProgress={submitting} className={css.submitButton}>
          {submitting ? 'Redirecting…' : `Send Gift — $${totalPrice.toFixed(2)}`}
        </Button>
      </form>
    </Modal>
  );
}
